import { Eye, EyeSlash } from '@phosphor-icons/react'
import { useAccountSecurity, useModal } from 'Hooks'
import { InputMessageError } from 'Organisms/InputMessageError'
import { PasswordRequirement } from 'Pages/Welcome/Components'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

const PASSWORD_PATTERN = /^(?=\S*[a-z])(?=\S*[A-Z])(?=\S*\d)(?=\S*([^\w\s]|[_]))\S{8,}$/

// Lets a user who signed up through Google (and so has never had a password)
// set one for the first time, so they can still log back in after
// disconnecting Google - there is no existing password to verify against.
const SetPasswordModal = ({ onClose, onSuccess }) => {
  const { Modal, hide } = useModal({ identifier: 'modalSetPassword', visible: true, setVisible: onClose })
  const { loadingAccountSecurityHook, setPasswordHook } = useAccountSecurity()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  const [error, setError] = useState('')
  const [passwordType, setPasswordType] = useState(true)
  const [confirmPasswordType, setConfirmPasswordType] = useState(true)

  const onSubmit = async (data) => {
    setError('')

    if (data.password !== data.passwordConfirm) {
      return setError('Passwords must match')
    }

    const res = await setPasswordHook({ password: data.password })

    if (!res.success) {
      return setError(res.message)
    }

    onSuccess()
    hide()
  }

  return (
    <Modal center>
      <div className="modal-content p-5" style={{ maxWidth: 450 }}>
        <div className="modal-header">
          <div className="row text-center gy-2">
            <div className="col-12">
              <h3 className="modal-title fs-5" id="modalSetPasswordLabel">Set a password</h3>
            </div>
            <div className="col-12">
              <p className="m-0">
                Your account currently signs in with Google only. Set a password so you can still log in once Google is disconnected.
              </p>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={() => hide()} />
        </div>

        <form className="modal-body d-grid" onSubmit={handleSubmit(onSubmit)}>
          {error && <InputMessageError error={error} />}
          {errors.password && (
            <InputMessageError error="Password must have at least 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character" />
          )}

          <div className="mb-4 position-relative">
            <label className="form-label d-flex align-items-center" htmlFor="inputSetPassword">
              Password
              <PasswordRequirement />
            </label>
            <input
              type={passwordType ? 'password' : 'text'}
              {...register('password', { required: true, pattern: PASSWORD_PATTERN })}
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              id="inputSetPassword"
              placeholder="Password"
            />
            <button
              type="button"
              className="btn btn-ghost position-absolute eye-button"
              onClick={() => setPasswordType(!passwordType)}>
              {!passwordType && <Eye size={18} weight="regular" />}
              {passwordType && <EyeSlash size={18} weight="regular" />}
            </button>
          </div>

          <div className="mb-4 position-relative">
            <label className="form-label" htmlFor="inputSetPasswordConfirm">
              Confirm password
            </label>
            <input
              type={confirmPasswordType ? 'password' : 'text'}
              {...register('passwordConfirm', { required: true })}
              className="form-control"
              id="inputSetPasswordConfirm"
              placeholder="Confirm password"
            />
            <button
              type="button"
              className="btn btn-ghost position-absolute eye-button"
              onClick={() => setConfirmPasswordType(!confirmPasswordType)}>
              {!confirmPasswordType && <Eye size={18} weight="regular" />}
              {confirmPasswordType && <EyeSlash size={18} weight="regular" />}
            </button>
          </div>

          <div className="row justify-content-between">
            <div className="col-auto">
              <button type="button" onClick={() => hide()} className="btn btn-secondary" disabled={loadingAccountSecurityHook}>
                Cancel
              </button>
            </div>
            <div className="col-auto">
              <button type="submit" className="btn btn-primary" disabled={loadingAccountSecurityHook}>
                {loadingAccountSecurityHook ? 'Saving...' : 'Set password'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default SetPasswordModal
